/**
 * Adverse Drug Reaction (ADR) Data Model
 * Compliance: TGA Blue Card reporting, ADHA My Health Record integration,
 * Aged Care Quality Standard 3 — Personal Care and Clinical Care.
 */

export const ADR_SEVERITY = {
  MILD: 'mild',
  MODERATE: 'moderate',
  SEVERE: 'severe',
  ANAPHYLAXIS: 'anaphylaxis',
};

export const ADR_SEVERITY_LABELS = {
  [ADR_SEVERITY.MILD]: 'Mild',
  [ADR_SEVERITY.MODERATE]: 'Moderate',
  [ADR_SEVERITY.SEVERE]: 'Severe',
  [ADR_SEVERITY.ANAPHYLAXIS]: 'Anaphylaxis',
};

export const ADR_SEVERITY_COLOURS = {
  [ADR_SEVERITY.MILD]: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  [ADR_SEVERITY.MODERATE]: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  [ADR_SEVERITY.SEVERE]: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
  [ADR_SEVERITY.ANAPHYLAXIS]: { bg: 'bg-red-200', text: 'text-red-900', border: 'border-red-500' },
};

export const ADR_STATUS = {
  ACTIVE: 'active',
  RESOLVED: 'resolved',
  MONITORING: 'monitoring',
};

export const ADR_ACTION = {
  DRUG_CEASED: 'drug_ceased',
  DOSE_REDUCED: 'dose_reduced',
  DRUG_CHANGED: 'drug_changed',
  ANTIHISTAMINE_GIVEN: 'antihistamine_given',
  ADRENALINE_GIVEN: 'adrenaline_given',
  MONITORING_INCREASED: 'monitoring_increased',
  HOSPITAL_TRANSFER: 'hospital_transfer',
  GP_NOTIFIED: 'gp_notified',
  TGA_REPORTED: 'tga_reported',
  OTHER: 'other',
};

export const ADR_ACTION_LABELS = {
  [ADR_ACTION.DRUG_CEASED]: 'Drug Ceased',
  [ADR_ACTION.DOSE_REDUCED]: 'Dose Reduced',
  [ADR_ACTION.DRUG_CHANGED]: 'Drug Changed',
  [ADR_ACTION.ANTIHISTAMINE_GIVEN]: 'Antihistamine Administered',
  [ADR_ACTION.ADRENALINE_GIVEN]: 'Adrenaline Administered',
  [ADR_ACTION.MONITORING_INCREASED]: 'Monitoring Increased',
  [ADR_ACTION.HOSPITAL_TRANSFER]: 'Transfer to Hospital',
  [ADR_ACTION.GP_NOTIFIED]: 'GP Notified',
  [ADR_ACTION.TGA_REPORTED]: 'Reported to TGA (Blue Card)',
  [ADR_ACTION.OTHER]: 'Other',
};

export const ADR_REPORTED_TO = {
  GP: 'gp',
  PHARMACIST: 'pharmacist',
  SPECIALIST: 'specialist',
  TGA: 'tga',
  FAMILY: 'family',
  CLINICAL_MANAGER: 'clinical_manager',
};

export const ADR_REPORTED_TO_LABELS = {
  [ADR_REPORTED_TO.GP]: 'General Practitioner',
  [ADR_REPORTED_TO.PHARMACIST]: 'Pharmacist',
  [ADR_REPORTED_TO.SPECIALIST]: 'Specialist',
  [ADR_REPORTED_TO.TGA]: 'TGA (Therapeutic Goods Administration)',
  [ADR_REPORTED_TO.FAMILY]: 'Family / Representative',
  [ADR_REPORTED_TO.CLINICAL_MANAGER]: 'Clinical Manager',
};

export const createAdverseDrugReaction = (data = {}) => {
  return {
    id: data.id || null,
    residentId: data.residentId || null,
    residentName: data.residentName || '',

    // Drug that caused the reaction
    drug: data.drug || '',
    drugGenericName: data.drugGenericName || '',
    drugDose: data.drugDose || '',
    medicationOrderId: data.medicationOrderId || null,

    // Reaction details
    reaction: data.reaction || '',
    reactionDetails: data.reactionDetails || '',
    severity: data.severity || ADR_SEVERITY.MILD,
    onsetTime: data.onsetTime || null,
    dateIdentified: data.dateIdentified || new Date().toISOString(),

    // Clinical response
    actionsTaken: data.actionsTaken || [],
    actionNotes: data.actionNotes || '',
    reportedTo: data.reportedTo || [],
    reportedBy: data.reportedBy || null,
    reportedByName: data.reportedByName || '',

    // TGA reporting
    tgaBlueCardSubmitted: data.tgaBlueCardSubmitted ?? false,
    tgaReferenceNumber: data.tgaReferenceNumber || '',
    tgaSubmissionDate: data.tgaSubmissionDate || null,

    // Follow-up
    status: data.status || ADR_STATUS.ACTIVE,
    resolvedDate: data.resolvedDate || null,
    followUpRequired: data.followUpRequired ?? true,
    followUpDate: data.followUpDate || null,
    followUpNotes: data.followUpNotes || '',

    // Link to allergy record (auto-populate allergy list)
    addedToAllergyList: data.addedToAllergyList ?? false,

    // Facility
    facilityId: data.facilityId || null,

    // Metadata
    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt || new Date().toISOString(),
    createdBy: data.createdBy || null,
  };
};

export const validateAdverseDrugReaction = (adr) => {
  const errors = [];

  if (!adr.residentId) {
    errors.push('Resident is required');
  }

  if (!adr.drug) {
    errors.push('Drug name is required');
  }

  if (!adr.reaction) {
    errors.push('Reaction description is required');
  }

  if (!adr.severity || !Object.values(ADR_SEVERITY).includes(adr.severity)) {
    errors.push('Valid severity level is required');
  }

  if (!adr.dateIdentified) {
    errors.push('Date identified is required');
  }

  if (!adr.reportedBy) {
    errors.push('Reported by (staff ID) is required');
  }

  if (adr.severity === ADR_SEVERITY.ANAPHYLAXIS || adr.severity === ADR_SEVERITY.SEVERE) {
    if (!adr.actionsTaken || adr.actionsTaken.length === 0) {
      errors.push('At least one action is required for severe/anaphylaxis reactions');
    }
    if (!adr.reportedTo || adr.reportedTo.length === 0) {
      errors.push('Severe/anaphylaxis reactions must be reported to at least one party');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
